/**
 * 🃏 DragoniousCCG — Phase 1 Collection & Pack Store (Feb 2026)
 *
 * Canon: /app/memory/DRAGONIOUS_CCG_CANON_FEB2026.md
 *
 * Ships:
 *   • Header stats: total collection %, per-class progress
 *   • Filter bar: set / class / rarity / owned-only
 *   • Binder-style card grid — 6 rarity frames + 7 class ribbons
 *   • Pack store — 5 tiers (Booster / Standard / Premium / Mythic /
 *     Daily Free) with multi-currency prices
 *   • Pack open reveal — card-flip animation, dupe refund banner
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Package, Search, Filter, X, Sparkles, BookOpen, CheckCircle2, AlertCircle } from "lucide-react";
import DFCLRulebook from "@/components/DFCLRulebook";
import PackRipReveal from "@/components/PackRipReveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Rarity frame colors (must mirror server-side RARITY_META) ──
const RARITY_FRAME = {
  common:    { ring: "ring-neutral-500/50",   glow: "shadow-neutral-500/20",   chip: "bg-neutral-700 text-white",       stars: "★"      },
  uncommon:  { ring: "ring-neutral-300/60",   glow: "shadow-neutral-300/25",   chip: "bg-neutral-500 text-white",       stars: "★★"     },
  rare:      { ring: "ring-blue-400/60",      glow: "shadow-blue-500/30",      chip: "bg-blue-700 text-white",          stars: "★★★"    },
  epic:      { ring: "ring-fuchsia-400/70",   glow: "shadow-fuchsia-500/40",   chip: "bg-fuchsia-700 text-white",       stars: "★★★★"   },
  legendary: { ring: "ring-amber-400/80",     glow: "shadow-amber-500/50",     chip: "bg-amber-600 text-white",         stars: "★★★★★"  },
  mythic:    { ring: "ring-red-500/90",       glow: "shadow-red-600/60",       chip: "bg-red-700 text-white",           stars: "★★★★★★" },
};

const CLASS_RIBBON = {
  hero:      { label: "Hero",      color: "bg-emerald-800/80" },
  villain:   { label: "Villain",   color: "bg-red-900/80"     },
  storyline: { label: "Storyline", color: "bg-indigo-800/80"  },
  move:      { label: "Skill",     color: "bg-slate-700/80"   },
  showtime:  { label: "Showtime",  color: "bg-amber-800/80"   },
  fatality:  { label: "Fatality",  color: "bg-red-800/90"     },
  harmony:   { label: "Harmony",   color: "bg-purple-800/80"  },
};

const CURRENCY_ICON = {
  omni_dollars:        "💎",
  unbound_omni:        "💎",   // legacy alias — stays for safety
  bound_omni_dollars:  "🔒",
  bound_omni:          "🔒",   // legacy alias
  tokens:              "🔥",
  phoenix_tokens:      "🔥",   // legacy alias
  phoenix_coins:       "🪙",
  chits:               "💰",
  free_daily:          "🆓",
};

// ── Individual card cell ──────────────────────────────────
function CCGCardCell({ card, onZoom }) {
  const frame = RARITY_FRAME[card.rarity] || RARITY_FRAME.common;
  const ribbon = CLASS_RIBBON[card.class] || CLASS_RIBBON.move;
  const owned = card.owned;
  const isSecret = !!card.hidden_endgame_secret;
  // Tapping an owned card zooms it. Locked cards remain silent — no
  // detail to show until you pull it from a pack.
  const clickable = owned && typeof onZoom === "function";
  const handleClick = () => { if (clickable) onZoom(card); };
  const handleKey = (e) => {
    if (!clickable) return;
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onZoom(card); }
  };
  return (
    <div
      className={`relative aspect-[2/3] rounded-lg border-2 overflow-hidden ${
        isSecret
          ? "prism-secret-frame bg-black"
          : `bg-gradient-to-br from-neutral-900 to-black ${owned ? `ring-2 ${frame.ring} shadow-lg ${frame.glow}` : "border-dashed border-neutral-800 opacity-70"}`
      } ${clickable ? "cursor-pointer transition hover:scale-[1.03]" : ""}`}
      data-testid={`ccg-card-${card.card_id}`}
      onClick={handleClick}
      onKeyDown={handleKey}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      title={clickable ? `Tap to zoom — ${card.name}` : undefined}
    >
      {/* Class ribbon — endgame secrets get a prismatic '??? ANOMALY' ribbon */}
      {isSecret ? (
        <div
          className="absolute top-0 left-0 right-0 py-0.5 text-[8px] uppercase tracking-widest font-bold text-white text-center"
          style={{
            background: "linear-gradient(90deg,#22d3ee,#a855f7,#f59e0b,#ec4899,#22d3ee)",
            backgroundSize: "300% 100%",
            animation: "prismShimmer 4s linear infinite",
          }}
        >
          ??? Endgame Anomaly
        </div>
      ) : (
        <div className={`absolute top-0 left-0 right-0 py-0.5 text-[8px] uppercase tracking-widest font-bold text-white text-center ${card.is_dfcl ? "bg-gradient-to-r from-amber-700/90 via-red-700/90 to-fuchsia-800/90" : ribbon.color}`}>
          {card.is_dfcl ? "🔥 Flame Legend" : ribbon.label}
        </div>
      )}

      {/* Icon + name — secrets keep icon silhouetted + halo */}
      <div className="pt-5 pb-2 px-2 text-center relative">
        {isSecret && (
          <div
            className="absolute left-1/2 -translate-x-1/2 top-4 w-16 h-16 rounded-full opacity-40 pointer-events-none"
            style={{
              background: "radial-gradient(circle,#a855f7 0%,#22d3ee 45%,transparent 70%)",
              filter: "blur(4px)",
            }}
          />
        )}
        <div
          className="text-4xl mb-1 relative"
          style={isSecret ? { filter: "drop-shadow(0 0 6px #a855f7) brightness(0.7) contrast(1.4)" } : undefined}
        >
          {card.icon || card.hero_icon || "🃏"}
        </div>
        <div className="text-[11px] font-bold text-white line-clamp-2 leading-tight">
          {owned ? card.name : "???"}
        </div>
        {isSecret && owned && card.title && (
          <div className="text-[9px] uppercase tracking-widest text-fuchsia-300 mt-0.5 line-clamp-1">
            {card.title}
          </div>
        )}
      </div>

      {/* Body — secrets show their tagline in italic instead of archetype */}
      <div className="px-2 pb-2 text-[9px] leading-relaxed line-clamp-4 relative">
        {owned ? (
          isSecret && card.tagline ? (
            <span className="italic text-fuchsia-200/80">&ldquo;{card.tagline}&rdquo;</span>
          ) : card.class === "move" || card.class === "showtime" || card.class === "fatality"
            ? <span className="text-neutral-400">{card.description}</span>
            : <span className="text-neutral-400">{card.lore || card.archetype || card.debut || ""}</span>
        ) : <span className="text-neutral-400">{card.is_dfcl ? "Locked — earn it in the Flame Card Legends binder." : "Locked — open a pack to reveal."}</span>}
      </div>

      {/* Bottom strip — rarity + id + qty (secrets get a SECRET chip) */}
      <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-1.5 py-1 bg-black/70 backdrop-blur-sm">
        <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${frame.chip}`}>{frame.stars}</span>
        {isSecret && owned ? (
          <span className="text-[8px] font-mono uppercase tracking-widest text-fuchsia-300 truncate mx-1 flex-1 text-center">
            Secret · {card.card_id}
          </span>
        ) : (
          <span className="text-[8px] font-mono text-neutral-500 truncate mx-1 flex-1 text-center">{card.card_id}</span>
        )}
        {owned && card.qty > 1 && (
          <span className="text-[9px] font-bold text-emerald-300 bg-emerald-900/50 px-1.5 py-0.5 rounded">×{card.qty}</span>
        )}
      </div>
    </div>
  );
}


// ── Card Zoom Modal — collector's full-screen view ──────
// Rarity yields for the duplicate-refund note (matches
// dragonious_ccg_service.RARITY_META). Kept in sync manually.
const RARITY_DUPE_YIELD = {
  common: 2, uncommon: 5, rare: 12, epic: 30, legendary: 80, mythic: 250,
};

function CardZoomModal({ card, onClose }) {
  if (!card) return null;
  const frame = RARITY_FRAME[card.rarity] || RARITY_FRAME.common;
  const ribbon = CLASS_RIBBON[card.class] || CLASS_RIBBON.move;
  const dupeYield = RARITY_DUPE_YIELD[card.rarity] || 0;
  const qty = card.qty || card.new_qty || 1;
  const isSecret = !!card.hidden_endgame_secret;

  // Body text — moves use description, heroes/villains use lore, storyline uses lore.
  const primary = (card.class === "move" || card.class === "showtime" || card.class === "fatality")
    ? card.description
    : (card.lore || "");

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="ccg-card-zoom"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-2xl border-2 border-fuchsia-500/50 bg-gradient-to-br from-purple-950/95 via-black/95 to-fuchsia-950/95 p-6"
        style={{ boxShadow: "0 0 90px rgba(217, 70, 239, 0.45)" }}
      >
        <button
          onClick={onClose}
          data-testid="ccg-card-zoom-close"
          className="absolute top-3 right-3 text-neutral-400 hover:text-white p-1"
        >
          <X size={22} />
        </button>

        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-6 items-start">
          {/* ── Zoomed card art ──────────────────────────── */}
          <div
            data-testid="ccg-card-zoom-art"
            className={`relative aspect-[2/3] rounded-xl border-2 overflow-hidden shadow-2xl mx-auto w-full max-w-[280px] ${
              isSecret
                ? "prism-secret-frame bg-black"
                : `bg-gradient-to-br from-neutral-900 to-black ring-2 ${frame.ring} ${frame.glow}`
            }`}
          >
            {isSecret ? (
              <div
                className="absolute top-0 left-0 right-0 py-1 text-[10px] uppercase tracking-widest font-bold text-white text-center"
                style={{
                  background: "linear-gradient(90deg,#22d3ee,#a855f7,#f59e0b,#ec4899,#22d3ee)",
                  backgroundSize: "300% 100%",
                  animation: "prismShimmer 4s linear infinite",
                }}
              >
                ??? Endgame Anomaly
              </div>
            ) : (
              <div className={`absolute top-0 left-0 right-0 py-1 text-[10px] uppercase tracking-widest font-bold text-white text-center ${ribbon.color}`}>
                {ribbon.label}
              </div>
            )}
            <div className="pt-8 pb-3 px-3 text-center relative">
              {isSecret && (
                <div
                  className="absolute left-1/2 -translate-x-1/2 top-6 w-32 h-32 rounded-full opacity-40 pointer-events-none"
                  style={{
                    background: "radial-gradient(circle,#a855f7 0%,#22d3ee 45%,transparent 70%)",
                    filter: "blur(6px)",
                  }}
                />
              )}
              <div
                className="text-7xl mb-2 leading-none relative"
                style={isSecret ? { filter: "drop-shadow(0 0 12px #a855f7) brightness(0.7) contrast(1.4)" } : undefined}
              >
                {card.icon || card.hero_icon || "🃏"}
              </div>
              <div className="text-base font-bold text-white leading-tight">{card.name}</div>
              {isSecret && card.title && (
                <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 mt-1">
                  {card.title}
                </div>
              )}
            </div>
            <div className="px-3 text-[11px] leading-relaxed line-clamp-6">
              {isSecret && card.tagline ? (
                <span className="italic text-fuchsia-200/85">&ldquo;{card.tagline}&rdquo;</span>
              ) : (
                <span className="text-neutral-300">{primary || card.archetype || card.debut || ""}</span>
              )}
            </div>
            <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-2 py-1.5 bg-black/70 backdrop-blur-sm">
              <span className={`text-[10px] px-2 py-0.5 rounded font-bold ${frame.chip}`}>{frame.stars}</span>
              <span className={`text-[9px] font-mono ${isSecret ? "text-fuchsia-300 uppercase tracking-widest" : "text-neutral-400"}`}>
                {isSecret ? `Secret · ${card.card_id}` : card.card_id}
              </span>
              {qty > 1 && (
                <span className="text-[10px] font-bold text-emerald-300 bg-emerald-900/50 px-2 py-0.5 rounded">
                  ×{qty}
                </span>
              )}
            </div>
          </div>

          {/* ── Card metadata & lore ─────────────────────── */}
          <div className="space-y-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.35em] text-fuchsia-300 font-bold">
                {isSecret ? `??? Endgame Anomaly · ${card.rarity}` : `${ribbon.label} · ${card.rarity}`}
              </div>
              <div className="text-2xl font-bold text-white mt-0.5" data-testid="ccg-card-zoom-name">{card.name}</div>
              {isSecret && card.title && (
                <div className="text-sm text-fuchsia-300 italic mt-0.5">{card.title}</div>
              )}
              <div className="text-[10px] font-mono text-neutral-500 mt-0.5">{card.card_id}</div>
            </div>

            {/* 🌌 Hidden endgame secret — tagline callout + redacted-dossier notice */}
            {isSecret && card.tagline && (
              <div
                className="rounded-lg p-3 border-2"
                style={{
                  borderImage: "linear-gradient(90deg,#22d3ee,#a855f7,#f59e0b,#ec4899) 1",
                  background: "linear-gradient(135deg, rgba(34,211,238,0.08), rgba(168,85,247,0.10), rgba(236,72,153,0.08))",
                }}
                data-testid="ccg-card-zoom-tagline"
              >
                <div className="text-[9px] uppercase tracking-widest text-fuchsia-300 mb-1">Tagline</div>
                <div className="text-base italic text-fuchsia-100 leading-relaxed">&ldquo;{card.tagline}&rdquo;</div>
              </div>
            )}
            {isSecret && (
              <div className="rounded-lg border border-dashed border-fuchsia-500/40 bg-black/60 p-3" data-testid="ccg-card-zoom-redacted">
                <div className="text-[9px] uppercase tracking-widest text-fuchsia-300 mb-1">⚠ Dossier Redacted</div>
                <div className="text-xs text-neutral-300 leading-relaxed">
                  The full dossier for this fighter is sealed behind an
                  endgame encounter that has not yet been unlocked.
                  Only this card and its tagline are known to exist.
                </div>
              </div>
            )}

            {/* Hero attribution for move / showtime / fatality cards */}
            {(card.hero_name || card.hero_icon) && (
              <div className="rounded-lg border border-neutral-800 bg-black/40 p-3" data-testid="ccg-card-zoom-hero">
                <div className="text-[9px] uppercase tracking-widest text-neutral-500 mb-1">Belongs to</div>
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{card.hero_icon || "🥋"}</span>
                  <span className="text-sm font-bold text-white">{card.hero_name}</span>
                </div>
              </div>
            )}

            {/* Move inputs — PC + Mobile if present */}
            {card.command_input && (
              <div className="rounded-lg border border-emerald-500/40 bg-emerald-900/20 p-3" data-testid="ccg-card-zoom-input">
                <div className="text-[9px] uppercase tracking-widest text-emerald-300 mb-1">Command Input</div>
                <div className="font-mono text-emerald-200 text-sm">▶ {card.command_input}</div>
              </div>
            )}

            {/* Lore / description */}
            {primary && (
              <div className="rounded-lg border border-neutral-800 bg-black/40 p-3" data-testid="ccg-card-zoom-lore">
                <div className="text-[9px] uppercase tracking-widest text-neutral-500 mb-1">
                  {(card.class === "move" || card.class === "showtime" || card.class === "fatality") ? "Description" : "Lore"}
                </div>
                <div className="text-sm text-neutral-200 leading-relaxed whitespace-pre-line">{primary}</div>
              </div>
            )}

            {/* Archetype / debut for heroes & villains */}
            {(card.archetype || card.debut) && (
              <div className="grid grid-cols-2 gap-2">
                {card.archetype && (
                  <div className="rounded-lg border border-neutral-800 bg-black/40 p-2">
                    <div className="text-[9px] uppercase tracking-widest text-neutral-500">Archetype</div>
                    <div className="text-xs text-white mt-0.5">{card.archetype}</div>
                  </div>
                )}
                {card.debut && (
                  <div className="rounded-lg border border-neutral-800 bg-black/40 p-2">
                    <div className="text-[9px] uppercase tracking-widest text-neutral-500">Debut Set</div>
                    <div className="text-xs text-white mt-0.5 capitalize">{card.debut}</div>
                  </div>
                )}
              </div>
            )}

            {/* Storyline tie-in */}
            {card.hero_tie_in && (
              <div className="rounded-lg border border-indigo-500/40 bg-indigo-900/20 p-3">
                <div className="text-[9px] uppercase tracking-widest text-indigo-300 mb-1">Hero Tie-in</div>
                <div className="text-sm text-indigo-100">{card.hero_tie_in}</div>
              </div>
            )}

            {/* Duplicate summary */}
            <div className="rounded-lg border border-yellow-500/40 bg-yellow-900/15 p-3" data-testid="ccg-card-zoom-dupes">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="text-[9px] uppercase tracking-widest text-yellow-300 mb-0.5">Copies Owned</div>
                  <div className="text-2xl font-bold text-yellow-200 font-mono" data-testid="ccg-card-zoom-qty">×{qty}</div>
                </div>
                {qty > 1 && dupeYield > 0 && (
                  <div className="text-right">
                    <div className="text-[9px] uppercase tracking-widest text-yellow-300 mb-0.5">Dupes Refunded</div>
                    <div className="text-lg font-bold text-yellow-200">
                      💎 {(qty - 1) * dupeYield}
                    </div>
                    <div className="text-[9px] text-neutral-500">Unbound OD from this card</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


// Bulk purchase presets — server clamps to BULK_MAX_QUANTITY (25)
const BULK_QUANTITY_PRESETS = [1, 5, 10];
const BULK_MAX_CAP = 25;

// ── Pack Store card ──────────────────────────────────────
function PackStoreCard({ pack, balances, onBuy, opening, dailyAvailable }) {
  const cur = pack.price?.currency;
  const amount = pack.price?.amount;
  const balance = balances?.[cur] ?? 0;
  const isDaily = pack.id === "daily_free";
  const [qty, setQty] = useState(1);

  // "Max" = how many packs the wallet can afford, capped at BULK_MAX_CAP
  const maxAffordable = isDaily
    ? 1
    : Math.min(BULK_MAX_CAP, Math.max(0, Math.floor(balance / Math.max(1, amount))));

  const totalCost = amount * qty;
  const hasFunds = isDaily ? dailyAvailable : (balance >= totalCost);

  // 🪙 Owner canon Oct 2026 — Unbound OD can cover shortfalls on
  // bound_omni-priced packs. Backend stamps these fields on the pack.
  const unboundFallbackAvailable = !isDaily && !!pack.unbound_fallback_available;
  const unboundFallbackAmount    = !isDaily ? (pack.unbound_fallback_amount || 0) * qty : 0;
  // "Affordable" = either pure primary-currency OR unbound fallback kicks in.
  const unboundBal = balances?.omni_dollars ?? balances?.unbound_omni ?? 0;
  const canPayWithFallback = unboundFallbackAvailable && unboundBal >= unboundFallbackAmount;
  const effectivelyAffordable = hasFunds || canPayWithFallback;

  const handleClick = () => {
    if (opening) return;
    if (isDaily && !dailyAvailable) {
      toast.error("Free Daily already claimed — resets at PT midnight.");
      return;
    }
    if (!isDaily && balance < totalCost) {
      // 🪙 Try the Unbound OD fallback path first — but confirm first so
      // the player never spends Unbound by accident.
      if (canPayWithFallback) {
        const ok = window.confirm(
          `This pack costs ${totalCost} ${pack.price.label}.\n` +
          `You only have ${balance}. Spend ${unboundFallbackAmount} Unbound OD ` +
          `(you have ${unboundBal}) to cover the rest?\n\n` +
          `Unbound OD is tradeable and giftable — confirm you really want to spend it.`
        );
        if (!ok) return;
        onBuy(pack.id, qty, { useUnboundFallback: true });
        return;
      }
      const need = totalCost - balance;
      toast.error(`Need ${need} more ${pack.price.label} to open ${qty}× ${pack.name}.`);
      return;
    }
    onBuy(pack.id, isDaily ? 1 : qty);
  };

  const pickQty = (n) => {
    if (n < 1) n = 1;
    if (n > BULK_MAX_CAP) n = BULK_MAX_CAP;
    setQty(n);
  };

  return (
    <div className="rounded-xl border-2 border-neutral-800 hover:border-fuchsia-500/40 bg-gradient-to-br from-neutral-900/60 to-black/60 p-4 transition" data-testid={`ccg-pack-store-${pack.id}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-3xl">{pack.icon}</span>
        <div className="min-w-0">
          <div className="font-bold text-white text-sm">{pack.name}</div>
          <div className="text-[10px] text-neutral-400">{pack.cards} cards</div>
        </div>
      </div>
      <div className="text-[11px] text-neutral-400 mb-3 h-9 line-clamp-2">{pack.description}</div>

      {/* Quantity picker (hidden for the daily free gate) */}
      {!isDaily && (
        <div className="mb-3" data-testid={`ccg-pack-qty-picker-${pack.id}`}>
          <div className="text-[9px] uppercase tracking-widest text-neutral-500 mb-1">Quantity</div>
          <div className="flex items-center gap-1">
            {BULK_QUANTITY_PRESETS.map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => pickQty(n)}
                data-testid={`ccg-pack-qty-${pack.id}-${n}`}
                className={`flex-1 py-1 rounded text-[10px] font-bold uppercase tracking-widest transition ${
                  qty === n
                    ? "bg-fuchsia-600 text-white"
                    : "bg-neutral-800 text-neutral-300 hover:bg-neutral-700"
                }`}
              >
                {n}×
              </button>
            ))}
            <button
              type="button"
              onClick={() => pickQty(Math.max(1, maxAffordable))}
              disabled={maxAffordable < 1}
              data-testid={`ccg-pack-qty-${pack.id}-max`}
              className={`flex-1 py-1 rounded text-[10px] font-bold uppercase tracking-widest transition ${
                qty === maxAffordable && maxAffordable >= 1
                  ? "bg-fuchsia-600 text-white"
                  : maxAffordable < 1
                    ? "bg-neutral-900 text-neutral-600 cursor-not-allowed"
                    : "bg-neutral-800 text-neutral-300 hover:bg-neutral-700"
              }`}
              title={maxAffordable < 1 ? "Not enough currency" : `Max affordable: ${maxAffordable}`}
            >
              Max
            </button>
          </div>
        </div>
      )}

      <div className="mb-3">
        <div className="text-[9px] uppercase tracking-widest text-neutral-500 mb-0.5">
          {isDaily || qty === 1 ? "Price" : `Total (${qty}×)`}
        </div>
        <div className="text-base font-bold text-white flex items-center gap-1">
          <span>{CURRENCY_ICON[cur] || ""}</span>
          {isDaily ? (
            <span className={dailyAvailable ? "text-emerald-300" : "text-neutral-500"}>
              {dailyAvailable ? "Ready" : "Claimed"}
            </span>
          ) : (
            <span data-testid={`ccg-pack-total-${pack.id}`}>
              {totalCost.toLocaleString()} <span className="text-xs text-neutral-400 font-normal">{pack.price.label}</span>
            </span>
          )}
        </div>
        {!isDaily && (
          <div
            className={`mt-1 flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded w-fit ${
              balance >= totalCost
                ? "bg-emerald-900/40 text-emerald-300"
                : "bg-red-900/40 text-red-300"
            }`}
            data-testid={`ccg-pack-balance-${pack.id}`}
          >
            {balance >= totalCost ? <CheckCircle2 size={10} /> : <AlertCircle size={10} />}
            <span>
              You have {balance.toLocaleString()}
              {balance < totalCost && ` · need ${(totalCost - balance).toLocaleString()} more`}
            </span>
          </div>
        )}
      </div>
      <button
        onClick={handleClick}
        disabled={opening || (!isDaily && !effectivelyAffordable) || (isDaily && !dailyAvailable)}
        data-testid={`ccg-pack-buy-${pack.id}`}
        className={`w-full py-2 rounded-lg text-xs font-bold uppercase tracking-widest transition ${
          opening
            ? "bg-neutral-800 text-neutral-500 cursor-wait"
            : (isDaily ? !dailyAvailable : !effectivelyAffordable)
              ? "bg-neutral-900 text-neutral-600 cursor-not-allowed border border-neutral-800"
              : "bg-gradient-to-r from-fuchsia-600 to-purple-600 text-white hover:brightness-125 shadow-lg shadow-fuchsia-900/30"
        }`}
      >
        {opening
          ? "Opening…"
          : isDaily
            ? (dailyAvailable ? "Claim Free" : "Come back tomorrow")
            : (hasFunds
                ? (qty > 1 ? `Open ${qty} Packs` : "Open Pack")
                : canPayWithFallback
                  ? `Pay ${unboundFallbackAmount} Unbound OD`
                  : "Insufficient Funds")}
      </button>
    </div>
  );
}

// ── Main Component ───────────────────────────────────────
export default function DragoniousCCG({ onJumpToTab }) {
  const [state,   setState]   = useState(null);
  const [cards,   setCards]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(false);
  const [reveal,  setReveal]  = useState(null);
  // 🔍 Full-screen card zoom for the collector's view.
  const [zoomCard, setZoomCard] = useState(null);
  // 📖 DFCL Official Rules overlay — Roc 2026-09-17 canon.
  const [showRules, setShowRules] = useState(false);

  // Filters
  const [fSet,    setFSet]    = useState("");
  const [fClass,  setFClass]  = useState("");
  const [fRarity, setFRarity] = useState("");
  const [ownedOnly, setOwnedOnly] = useState(false);
  const [search, setSearch]   = useState("");

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        axios.get(`${API}/ccg/state`, { withCredentials: true }),
        axios.get(`${API}/ccg/collection`, { withCredentials: true }),
      ]);
      setState(s.data);
      setCards(c.data.cards || []);
    } catch (e) {
      toast.error("Failed to load the Card Collection");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const buyPack = async (packId, quantity = 1, opts = {}) => {
    if (opening) return;
    setOpening(true);
    try {
      const r = await axios.post(
        `${API}/ccg/open-pack`,
        {
          pack_id: packId,
          quantity,
          use_unbound_fallback: !!opts.useUnboundFallback,
        },
        { withCredentials: true }
      );
      // 🪙 If the server actually dipped into Unbound OD, surface it.
      const used = r.data?.unbound_fallback_used || 0;
      if (used > 0) {
        toast.success(`Spent ${used} Unbound OD to cover the shortfall.`);
      }
      setReveal(r.data);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Pack open failed");
    } finally {
      setOpening(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return cards.filter((c) => {
      if (fSet && c.set !== fSet) return false;
      if (fClass && c.class !== fClass) return false;
      if (fRarity && c.rarity !== fRarity) return false;
      if (ownedOnly && !c.owned) return false;
      if (q && !(c.name || "").toLowerCase().includes(q) && !(c.card_id || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [cards, fSet, fClass, fRarity, ownedOnly, search]);

  if (loading || !state) {
    return <div className="text-center py-16 text-neutral-500" data-testid="ccg-loading">Loading the Card Vault…</div>;
  }

  return (
    <div className="max-w-6xl mx-auto" data-testid="ccg-root">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 border border-fuchsia-500/40 text-fuchsia-300 mb-3">
          <Sparkles size={14} />
          <span className="font-accent text-xs tracking-[0.3em] uppercase">Dragonious CCG · Phase 1</span>
        </div>
        <h2 className="text-3xl md:text-5xl font-bold text-white leading-[0.9] mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
          The <span className="text-fuchsia-400">Dragonious</span> Collection
        </h2>
        <p className="text-neutral-400 max-w-2xl mx-auto text-sm">
          Every canonical Hero, Villain, Move, Showtime, Fatality, Storyline, and Harmony —
          across Core, Phoenix Flame, and Fighters — enshrined as collectible cards.
        </p>

        {/* 🔗 Cross-link to the DFCL rules (Oct 2026 — the Collection
            is a Pokédex, DFCL is the card fighting game. The rulebook
            lives inside this overlay too so collectors don't have to
            hop tabs; the full DFCL home is one tab over). */}
        <div className="mt-3 flex justify-center">
          <button
            onClick={() => setShowRules(true)}
            data-testid="ccg-learn-rules"
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-amber-400/80 hover:text-amber-200 transition-colors group"
          >
            <BookOpen size={12} className="opacity-70 group-hover:opacity-100" />
            <span>Learn the rules — Flame Card Legends →</span>
          </button>
        </div>
      </div>

      {/* 📖 Overlay — rules modal (kept locally so Cards users can read
          the rules without hopping tabs, but DFCL tab is now the home). */}
      {showRules && <DFCLRulebook onClose={() => setShowRules(false)} />}

      {/* Collection Progress */}
      <div className="rounded-2xl border-2 border-fuchsia-500/30 bg-gradient-to-br from-fuchsia-950/30 via-black/60 to-purple-950/30 p-5 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-fuchsia-300 mb-1">Overall Collection</div>
            <div className="text-4xl font-bold text-white">
              {state.owned_unique}<span className="text-neutral-500 text-2xl"> / {state.catalog_total}</span>
            </div>
            <div className="text-xs text-fuchsia-200/70">{state.completion_pct}% complete · {state.owned_total} total cards owned</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-1">Packs Opened</div>
            <div className="text-3xl font-bold text-white">{state.packs_opened_total}</div>
          </div>
        </div>

        <div className="w-full h-2 bg-black/50 rounded-full overflow-hidden mb-4">
          <div
            className="h-full bg-gradient-to-r from-fuchsia-500 to-purple-500 transition-all"
            style={{ width: `${state.completion_pct}%` }}
          />
        </div>

        {/* Per-class progress */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
          {Object.entries(state.by_class || {}).map(([cls, info]) => (
            <div key={cls} className="rounded-lg border border-neutral-800 bg-black/40 p-2 text-center" data-testid={`ccg-class-stat-${cls}`}>
              <div className="text-[9px] uppercase tracking-widest text-neutral-500">{info.label}</div>
              <div className="text-lg font-bold text-white">{info.owned}<span className="text-neutral-600 text-sm"> / {info.total}</span></div>
            </div>
          ))}
        </div>
      </div>

      {/* Pack Store */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-3">
          <Package size={16} className="text-fuchsia-300" />
          <h3 className="text-[10px] uppercase tracking-[0.3em] text-fuchsia-300 font-bold">Pack Store</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="ccg-pack-store">
          {Object.values(state.pack_store || {}).map((pack) => (
            <PackStoreCard
              key={pack.id}
              pack={pack}
              balances={state.balances}
              onBuy={buyPack}
              opening={opening}
              dailyAvailable={state.daily_available}
            />
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-neutral-800 bg-black/40 p-3 mb-4 flex flex-wrap items-center gap-2" data-testid="ccg-filter-bar">
        <Filter size={14} className="text-neutral-400" />
        <div className="relative flex-1 min-w-[180px]">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or DRG-ID…"
            data-testid="ccg-filter-search"
            className="w-full pl-8 pr-2 py-1.5 rounded-lg bg-black/60 border border-neutral-800 text-white text-xs focus:border-fuchsia-500 outline-none"
          />
        </div>
        <select value={fClass} onChange={(e) => setFClass(e.target.value)} data-testid="ccg-filter-class"
          className="px-2 py-1.5 rounded-lg bg-black/60 border border-neutral-800 text-white text-xs">
          <option value="">All classes</option>
          {Object.entries(CLASS_RIBBON).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={fRarity} onChange={(e) => setFRarity(e.target.value)} data-testid="ccg-filter-rarity"
          className="px-2 py-1.5 rounded-lg bg-black/60 border border-neutral-800 text-white text-xs">
          <option value="">All rarities</option>
          {Object.entries(RARITY_FRAME).map(([k, v]) => <option key={k} value={k}>{v.stars} {k}</option>)}
        </select>
        <select value={fSet} onChange={(e) => setFSet(e.target.value)} data-testid="ccg-filter-set"
          className="px-2 py-1.5 rounded-lg bg-black/60 border border-neutral-800 text-white text-xs">
          <option value="">All sets</option>
          <option value="core">Core</option>
          <option value="phoenix">Phoenix Flame</option>
          <option value="fights">Fighters</option>
          <option value="dfcl">Flame Card Legends</option>
        </select>
        <label className="flex items-center gap-1.5 text-xs text-neutral-300 cursor-pointer">
          <input type="checkbox" checked={ownedOnly} onChange={(e) => setOwnedOnly(e.target.checked)} data-testid="ccg-filter-owned" />
          Owned only
        </label>
      </div>

      {/* Card Grid */}
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2" data-testid="ccg-filtered-count">
        Showing {filtered.length} of {cards.length} cards
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-8" data-testid="ccg-card-grid">
        {filtered.map((c) => (
          <CCGCardCell key={c.card_id} card={c} onZoom={setZoomCard} />
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full text-center py-10 text-neutral-500 text-sm">
            No cards match these filters.
          </div>
        )}
      </div>

      <PackRipReveal result={reveal} onClose={() => setReveal(null)} />
      <CardZoomModal card={zoomCard} onClose={() => setZoomCard(null)} />
    </div>
  );
}
